import { useState, useCallback, useRef, createContext, useContext, type DragEvent } from "react"
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  Controls,
  addEdge,
  useNodesState,
  useEdgesState,
  useReactFlow,
  Handle,
  Position,
  type Node,
  type Edge,
  type Connection,
  type NodeProps,
  type NodeTypes,
} from "@xyflow/react"
import "@xyflow/react/dist/style.css"
import {
  Plus,
  Zap,
  Pencil,
  Trash2,
  X,
  FileText,
  RefreshCw,
  ArrowRightLeft,
  Upload,
  Flag,
  ShieldCheck,
  Bell,
  UserPlus,
  ArrowLeft,
  GitBranch,
  Eye,
  UserCheck,
  PenTool,
  CreditCard,
  XCircle,
  Clock,
  CalendarCheck,
} from "lucide-react"
import { Button } from "@/components/ui/Button"
import { notifyFeature } from "@/utils/demoNotifications"
import { cn } from "@/utils/Functions"

// ── Types ──────────────────────────────────────────────────────────────

type BlockConfig = {
  field: string
  operator: string
  value: string
  role: string
  days: string
  milestone: string
}

type BlockDef = {
  label: string
  icon: typeof FileText
  keyword: string
  color: string
  section: string
  configType: "none" | "condition" | "role" | "timing" | "milestone"
}

type CanvasBlock = {
  id: string
  variant: string
  config: BlockConfig
}

type Rule = {
  id: string
  name: string
  description: string
  blocks: CanvasBlock[]
  active: boolean
}

type BlockNodeData = {
  variant: string
  config: BlockConfig
  label: string
  [key: string]: unknown
}

// ── Block definitions ──────────────────────────────────────────────────

const BLOCK_DEFS: Record<string, BlockDef> = {
  // Events
  contract_created:      { label: "Contract Created",       icon: FileText,       keyword: "when",    color: "bg-violet-500",  section: "events",        configType: "none" },
  contract_updated:      { label: "Contract Updated",       icon: RefreshCw,      keyword: "when",    color: "bg-violet-500",  section: "events",        configType: "none" },
  deal_stage_changed:    { label: "Stage Changed",          icon: ArrowRightLeft, keyword: "when",    color: "bg-violet-500",  section: "events",        configType: "none" },
  document_uploaded:     { label: "Doc Uploaded",            icon: Upload,         keyword: "when",    color: "bg-violet-500",  section: "events",        configType: "none" },
  milestone_approaching: { label: "Milestone Approaching",  icon: Clock,          keyword: "when",    color: "bg-violet-500",  section: "events",        configType: "timing" },
  on_milestone:          { label: "On Milestone",            icon: CalendarCheck,  keyword: "when",    color: "bg-violet-500",  section: "events",        configType: "milestone" },
  // Conditions
  condition:             { label: "Condition",               icon: GitBranch,      keyword: "if",      color: "bg-amber-500",   section: "conditions",    configType: "condition" },
  // Approvals
  get_review:            { label: "Get Review",              icon: Eye,            keyword: "review",  color: "bg-blue-500",    section: "approvals",     configType: "role" },
  get_approval:          { label: "Get Approval",            icon: UserCheck,      keyword: "approve", color: "bg-blue-500",    section: "approvals",     configType: "role" },
  // Notifications
  send_notification:     { label: "Send Notification",       icon: Bell,           keyword: "notify",  color: "bg-cyan-600",    section: "notifications", configType: "role" },
  // Gates & Actions
  enable_signing:        { label: "Enable Signing",          icon: PenTool,        keyword: "gate",    color: "bg-emerald-500", section: "gates",         configType: "none" },
  enable_autopay:        { label: "Enable Autopay",          icon: CreditCard,     keyword: "gate",    color: "bg-emerald-500", section: "gates",         configType: "none" },
  auto_renew:            { label: "Auto-Renew Payment",      icon: RefreshCw,      keyword: "then",    color: "bg-emerald-500", section: "gates",         configType: "none" },
  auto_cancel:           { label: "Auto-Cancel",             icon: XCircle,        keyword: "then",    color: "bg-emerald-500", section: "gates",         configType: "none" },
  flag_review:           { label: "Flag for Review",         icon: Flag,           keyword: "then",    color: "bg-emerald-500", section: "gates",         configType: "none" },
  assign_reviewer:       { label: "Assign Reviewer",         icon: UserPlus,       keyword: "then",    color: "bg-emerald-500", section: "gates",         configType: "role" },
}

const PALETTE_SECTIONS = [
  { title: "Events",          headerColor: "text-violet-400", items: ["contract_created", "contract_updated", "deal_stage_changed", "document_uploaded", "milestone_approaching", "on_milestone"] },
  { title: "Conditions",      headerColor: "text-amber-400",  items: ["condition"] },
  { title: "Approvals",       headerColor: "text-blue-400",   items: ["get_review", "get_approval"] },
  { title: "Notifications",   headerColor: "text-cyan-400",   items: ["send_notification"] },
  { title: "Gates & Actions", headerColor: "text-emerald-400", items: ["enable_signing", "enable_autopay", "auto_renew", "auto_cancel", "flag_review", "assign_reviewer"] },
]

const ROLE_OPTIONS = ["Legal", "Finance", "A&R", "GM", "Agent", "Artist", "Manager", "Executive"]
const MILESTONE_OPTIONS = ["Renewal Deadline", "Delivery Date", "Option Period End", "Payment Due", "Advance Payment", "Rights Reversion"]
const FIELD_OPTIONS = ["Royalty Rate", "Advance Amount", "Deal Type", "Term Length", "Document Type", "Contract Value", "Territory", "Artist Tier"]
const OPERATOR_OPTIONS = ["equals", "not equals", "greater than", "less than", "contains", "starts with"]

const DEFAULT_CONFIG: BlockConfig = { field: "", operator: "equals", value: "", role: "", days: "", milestone: "" }

function getDef(variant: string): BlockDef {
  return BLOCK_DEFS[variant] ?? BLOCK_DEFS.condition
}

function rolePrefix(variant: string) {
  return variant === "send_notification" || variant === "assign_reviewer" ? "to" : "from"
}

// ── Color maps for inline styles (React Flow nodes need real colors) ──

const COLOR_MAP: Record<string, { bg: string; border: string }> = {
  "bg-violet-500":  { bg: "#8b5cf6", border: "#7c3aed" },
  "bg-amber-500":   { bg: "#f59e0b", border: "#d97706" },
  "bg-blue-500":    { bg: "#3b82f6", border: "#2563eb" },
  "bg-cyan-600":    { bg: "#0891b2", border: "#0e7490" },
  "bg-emerald-500": { bg: "#10b981", border: "#059669" },
}

function getColors(variant: string) {
  const def = getDef(variant)
  return COLOR_MAP[def.color] ?? { bg: "#6b7280", border: "#4b5563" }
}

// ── Preset rules ───────────────────────────────────────────────────────

const makeBlock = (variant: string, partial?: Partial<BlockConfig>): CanvasBlock => ({
  id: `preset-${Math.random().toString(36).slice(2, 8)}`,
  variant,
  config: { ...DEFAULT_CONFIG, ...partial },
})

const PRESET_RULES: Rule[] = [
  {
    id: "1",
    name: "Recording Contract Approval Chain",
    description: "Multi-step approval flow for new recording contracts through Legal, Finance, and GM",
    blocks: [
      makeBlock("contract_created"),
      makeBlock("get_review", { role: "Legal" }),
      makeBlock("get_review", { role: "Agent" }),
      makeBlock("get_approval", { role: "Finance" }),
      makeBlock("enable_signing"),
      makeBlock("get_approval", { role: "GM" }),
      makeBlock("enable_autopay"),
    ],
    active: true,
  },
  {
    id: "2",
    name: "Renewal Deadline Advance Notice",
    description: "Auto-notify and get GM approval 30 days before renewal, then auto-renew on deadline",
    blocks: [
      makeBlock("milestone_approaching", { days: "30", milestone: "Renewal Deadline" }),
      makeBlock("send_notification", { role: "GM" }),
      makeBlock("get_approval", { role: "GM" }),
      makeBlock("on_milestone", { milestone: "Renewal Deadline" }),
      makeBlock("auto_renew"),
    ],
    active: true,
  },
  {
    id: "3",
    name: "High Royalty Rate Review",
    description: "Flag contracts with royalty rates above 20% for Legal review",
    blocks: [
      makeBlock("contract_created"),
      makeBlock("condition", { field: "Royalty Rate", operator: "greater than", value: "20%" }),
      makeBlock("get_review", { role: "Legal" }),
      makeBlock("flag_review"),
    ],
    active: true,
  },
  {
    id: "4",
    name: "Recording Deal Compliance",
    description: "Require Legal + Finance approval for all recording deals before signing",
    blocks: [
      makeBlock("deal_stage_changed"),
      makeBlock("condition", { field: "Deal Type", operator: "equals", value: "Recording" }),
      makeBlock("get_approval", { role: "Legal" }),
      makeBlock("get_approval", { role: "Finance" }),
      makeBlock("enable_signing"),
    ],
    active: true,
  },
  {
    id: "5",
    name: "Large Advance Alert",
    description: "Notify GM and Finance when advance exceeds $100K, require GM approval",
    blocks: [
      makeBlock("contract_created"),
      makeBlock("condition", { field: "Advance Amount", operator: "greater than", value: "$100,000" }),
      makeBlock("send_notification", { role: "GM" }),
      makeBlock("send_notification", { role: "Finance" }),
      makeBlock("get_approval", { role: "GM" }),
    ],
    active: false,
  },
]

// ── Helpers ─────────────────────────────────────────────────────────────

let _bid = 0
const nextBlockId = () => `cb-${Date.now()}-${++_bid}`

const inputCls = "bg-muted border border-border rounded px-1.5 py-0.5 text-[11px] text-foreground focus:outline-none focus:ring-1 focus:ring-primary/30 appearance-none cursor-pointer"

// ── Flow actions context (so custom nodes can call back) ───────────────

type FlowActions = {
  deleteNode: (id: string) => void
  updateNodeConfig: (id: string, config: BlockConfig) => void
}

const FlowActionsContext = createContext<FlowActions>({
  deleteNode: () => {},
  updateNodeConfig: () => {},
})

// ── Custom Block Node ──────────────────────────────────────────────────

function BlockNode({ id, data }: NodeProps<Node<BlockNodeData>>) {
  const { deleteNode, updateNodeConfig } = useContext(FlowActionsContext)
  const def = getDef(data.variant)
  const Icon = def.icon
  const colors = getColors(data.variant)

  const handleConfigChange = (newConfig: BlockConfig) => {
    updateNodeConfig(id, newConfig)
  }

  const renderConfig = () => {
    const config = data.config

    switch (def.configType) {
      case "condition":
        return (
          <div className="flex items-center gap-1 flex-wrap mt-1.5">
            <select value={config.field} onChange={(e) => handleConfigChange({ ...config, field: e.target.value })} className={cn(inputCls, "flex-1 min-w-0")}>
              <option value="">Field…</option>
              {FIELD_OPTIONS.map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
            <select value={config.operator} onChange={(e) => handleConfigChange({ ...config, operator: e.target.value })} className={cn(inputCls, "w-[90px]")}>
              {OPERATOR_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
            <input type="text" value={config.value} onChange={(e) => handleConfigChange({ ...config, value: e.target.value })} placeholder="Value" className={cn(inputCls, "w-16 placeholder:text-muted-foreground")} />
          </div>
        )
      case "role":
        return (
          <div className="flex items-center gap-1.5 mt-1.5 text-[11px]">
            <span className="text-muted-foreground text-[10px]">{rolePrefix(data.variant)}</span>
            <select value={config.role} onChange={(e) => handleConfigChange({ ...config, role: e.target.value })} className={inputCls}>
              <option value="">Select…</option>
              {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
        )
      case "timing":
        return (
          <div className="flex items-center gap-1.5 mt-1.5 text-[11px] flex-wrap">
            <input type="text" value={config.days} onChange={(e) => handleConfigChange({ ...config, days: e.target.value })} placeholder="30" className={cn(inputCls, "w-8 text-center placeholder:text-muted-foreground")} />
            <span className="text-muted-foreground text-[10px]">days before</span>
            <select value={config.milestone} onChange={(e) => handleConfigChange({ ...config, milestone: e.target.value })} className={inputCls}>
              <option value="">Milestone…</option>
              {MILESTONE_OPTIONS.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
        )
      case "milestone":
        return (
          <div className="flex items-center gap-1.5 mt-1.5 text-[11px]">
            <span className="text-muted-foreground">On</span>
            <select value={config.milestone} onChange={(e) => handleConfigChange({ ...config, milestone: e.target.value })} className={inputCls}>
              <option value="">Milestone…</option>
              {MILESTONE_OPTIONS.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
        )
      default:
        return null
    }
  }

  return (
    <div
      className="group/node rounded-2xl bg-card text-foreground min-w-[200px] max-w-[260px] border border-border shadow-sm"
      // style={{ borderLeftWidth: 4, borderLeftColor: colors.bg }}
    >
      <Handle type="target" position={Position.Top} className="!w-2 !h-2 !rounded-full !border-2 !border-card" style={{ background: colors.bg }} />

      <div className="px-3 pt-2 pb-0.5 flex items-center gap-1.5">
        <span className="text-[9px] font-bold uppercase tracking-widest" style={{ color: colors.bg }}>{def.keyword}</span>
        <div className="flex-1" />
        <button
          onClick={() => deleteNode(id)}
          className="p-0.5 rounded opacity-0 group-hover/node:opacity-100 hover:bg-muted transition-all text-muted-foreground"
        >
          <X className="size-3" />
        </button>
      </div>

      <div className="px-3 pb-2.5">
        <div className="flex items-center gap-1.5 text-xs font-semibold text-foreground">
          <Icon className="size-3.5 shrink-0" style={{ color: colors.bg }} />
          <span>{def.label}</span>
        </div>
        {renderConfig()}
      </div>

      <Handle type="source" position={Position.Bottom} className="!w-2 !h-2 !rounded-full !border-2 !border-card" style={{ background: colors.bg }} />
    </div>
  )
}

const nodeTypes: NodeTypes = { blockNode: BlockNode }

// ── Convert rule blocks <-> React Flow nodes/edges ─────────────────────

function blocksToFlow(blocks: CanvasBlock[]): { nodes: Node<BlockNodeData>[]; edges: Edge[] } {
  const nodes: Node<BlockNodeData>[] = blocks.map((block, i) => ({
    id: block.id,
    type: "blockNode",
    position: { x: 250, y: i * 140 },
    data: { variant: block.variant, config: { ...block.config }, label: getDef(block.variant).label },
  }))

  const edges: Edge[] = []
  for (let i = 0; i < blocks.length - 1; i++) {
    edges.push({
      id: `e-${blocks[i].id}-${blocks[i + 1].id}`,
      source: blocks[i].id,
      target: blocks[i + 1].id,
      type: "default",
      animated: true,
      style: { stroke: "#94a3b8", strokeWidth: 1.5 },
    })
  }

  return { nodes, edges }
}

function flowToBlocks(nodes: Node<BlockNodeData>[], edges: Edge[]): CanvasBlock[] {
  // Walk edges to determine order. Start from nodes with no incoming edge.
  const targetSet = new Set(edges.map((e) => e.target))
  const sourceToTarget = new Map<string, string[]>()
  for (const e of edges) {
    const existing = sourceToTarget.get(e.source) ?? []
    existing.push(e.target)
    sourceToTarget.set(e.source, existing)
  }

  const roots = nodes.filter((n) => !targetSet.has(n.id))
  // If no edges or no roots, fall back to position-based sort
  if (roots.length === 0 || edges.length === 0) {
    return [...nodes]
      .sort((a, b) => a.position.y - b.position.y)
      .map((n) => ({ id: n.id, variant: n.data.variant, config: { ...n.data.config } }))
  }

  // BFS from roots
  const ordered: string[] = []
  const visited = new Set<string>()
  const queue = roots.map((r) => r.id)
  while (queue.length > 0) {
    const current = queue.shift()!
    if (visited.has(current)) continue
    visited.add(current)
    ordered.push(current)
    const children = sourceToTarget.get(current) ?? []
    for (const child of children) {
      if (!visited.has(child)) queue.push(child)
    }
  }
  // Add any orphan nodes
  for (const n of nodes) {
    if (!visited.has(n.id)) ordered.push(n.id)
  }

  const nodeMap = new Map(nodes.map((n) => [n.id, n]))
  return ordered
    .filter((id) => nodeMap.has(id))
    .map((id) => {
      const n = nodeMap.get(id)!
      return { id: n.id, variant: n.data.variant, config: { ...n.data.config } }
    })
}

// ── Palette Item (native HTML drag) ────────────────────────────────────

function PaletteItem({ variant }: { variant: string }) {
  const def = getDef(variant)
  const colors = COLOR_MAP[def.color] ?? { bg: "#6b7280", border: "#4b5563" }

  const onDragStart = (e: DragEvent) => {
    e.dataTransfer.setData("application/reactflow-variant", variant)
    e.dataTransfer.effectAllowed = "move"
  }

  return (
    <div
      draggable
      onDragStart={onDragStart}
      className="flex items-center gap-2 rounded-lg px-3 py-2 text-[11px] font-medium text-foreground bg-card border border-border select-none cursor-grab active:cursor-grabbing hover:shadow-md hover:border-border/80 transition-all"
      // style={{ borderLeftWidth: 3, borderLeftColor: colors.bg }}
    >
      <def.icon className="size-3.5 shrink-0" style={{ color: colors.bg }} />
      <span className="truncate">{def.label}</span>
    </div>
  )
}

// ── Flow Builder Inner (needs ReactFlowProvider ancestor) ──────────────

function FlowBuilderInner({
  initialRule,
  onSave,
  onCancel,
}: {
  initialRule: Rule | null
  onSave: (r: Rule) => void
  onCancel: () => void
}) {
  const [name, setName] = useState(initialRule?.name ?? "")
  const [description, setDescription] = useState(initialRule?.description ?? "")
  const reactFlowWrapper = useRef<HTMLDivElement>(null)
  const { screenToFlowPosition } = useReactFlow()

  const initial = initialRule
    ? blocksToFlow(initialRule.blocks.map((b) => ({ ...b, id: nextBlockId(), config: { ...b.config } })))
    : { nodes: [], edges: [] }

  const [nodes, setNodes, onNodesChange] = useNodesState(initial.nodes)
  const [edges, setEdges, onEdgesChange] = useEdgesState(initial.edges)

  const onConnect = useCallback(
    (params: Connection) => {
      setEdges((eds) =>
        addEdge(
          {
            ...params,
            type: "default",
            animated: true,
            style: { stroke: "#94a3b8", strokeWidth: 1.5 },
          },
          eds,
        ),
      )
    },
    [setEdges],
  )

  const deleteNode = useCallback(
    (id: string) => {
      setNodes((nds) => nds.filter((n) => n.id !== id))
      setEdges((eds) => eds.filter((e) => e.source !== id && e.target !== id))
    },
    [setNodes, setEdges],
  )

  const updateNodeConfig = useCallback(
    (id: string, config: BlockConfig) => {
      setNodes((nds) =>
        nds.map((n) => (n.id === id ? { ...n, data: { ...n.data, config } } : n)),
      )
    },
    [setNodes],
  )

  const onDragOver = useCallback((e: DragEvent) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = "move"
  }, [])

  const onDrop = useCallback(
    (e: DragEvent) => {
      e.preventDefault()
      const variant = e.dataTransfer.getData("application/reactflow-variant")
      if (!variant || !BLOCK_DEFS[variant]) return

      const position = screenToFlowPosition({ x: e.clientX, y: e.clientY })
      const def = getDef(variant)
      const newNode: Node<BlockNodeData> = {
        id: nextBlockId(),
        type: "blockNode",
        position,
        data: { variant, config: { ...DEFAULT_CONFIG }, label: def.label },
      }
      setNodes((nds) => [...nds, newNode])
    },
    [screenToFlowPosition, setNodes],
  )

  const canSave = name.trim() && nodes.length >= 2

  const handleSave = () => {
    const blocks = flowToBlocks(nodes as Node<BlockNodeData>[], edges)
    onSave({
      id: initialRule?.id ?? String(Date.now()),
      name,
      description,
      blocks,
      active: initialRule?.active ?? true,
    })
  }

  const flowActions: FlowActions = { deleteNode, updateNodeConfig }

  return (
    <FlowActionsContext.Provider value={flowActions}>
      <div className="h-full flex flex-col bg-background">
        <header className="shrink-0 border-b border-border bg-card/50 px-4 py-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={onCancel}>
              <ArrowLeft className="size-4 mr-1" />
              Back
            </Button>
            <div className="h-5 w-px bg-border" />
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Rule name…"
              className="flex-1 bg-transparent text-sm font-semibold text-foreground placeholder:text-muted-foreground focus:outline-none"
            />
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Description…"
              className="flex-1 bg-transparent text-xs text-muted-foreground placeholder:text-muted-foreground/60 focus:outline-none"
            />
            <Button size="sm" disabled={!canSave} onClick={handleSave}>
              {initialRule ? "Save Changes" : "Save Rule"}
            </Button>
          </div>
        </header>

        <div className="flex flex-1 overflow-hidden">
          {/* Palette */}
          <div className="w-52 shrink-0 border-r border-border bg-card/30 overflow-y-auto p-3 space-y-4">
            <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground px-1">Blocks</p>
            {PALETTE_SECTIONS.map((section) => (
              <div key={section.title}>
                <p className={cn("text-[10px] font-semibold uppercase tracking-wider mb-1.5 px-1", section.headerColor)}>
                  {section.title}
                </p>
                <div className="space-y-1.5">
                  {section.items.map((v) => (
                    <PaletteItem key={v} variant={v} />
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* React Flow Canvas */}
          <div ref={reactFlowWrapper} className="flex-1">
            <ReactFlow
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onConnect={onConnect}
              onDragOver={onDragOver}
              onDrop={onDrop}
              nodeTypes={nodeTypes}
              fitView
              fitViewOptions={{ padding: 0.3 }}
              snapToGrid
              snapGrid={[16, 16]}
              connectionLineStyle={{ stroke: "#94a3b8", strokeWidth: 1.5 }}
              defaultEdgeOptions={{
                type: "default",
                animated: true,
                style: { stroke: "#94a3b8", strokeWidth: 1.5 },
              }}
              proOptions={{ hideAttribution: true }}
              className="bg-background"
            >
              <Background gap={16} size={1} color="hsl(var(--muted-foreground) / 0.08)" />
              <Controls
                showInteractive={false}
                className="!bg-card !border-border !rounded-lg !shadow-md [&>button]:!bg-card [&>button]:!border-border [&>button]:!text-foreground [&>button:hover]:!bg-muted"
              />
            </ReactFlow>
          </div>
        </div>
      </div>
    </FlowActionsContext.Provider>
  )
}

function FlowBuilder(props: { initialRule: Rule | null; onSave: (r: Rule) => void; onCancel: () => void }) {
  return (
    <ReactFlowProvider>
      <FlowBuilderInner {...props} />
    </ReactFlowProvider>
  )
}

// ── Compact block content (card view) ──────────────────────────────────

function CompactContent({ variant, config }: { variant: string; config: BlockConfig }) {
  const def = getDef(variant)
  const Icon = def.icon
  const pill = "bg-white/15 rounded-full px-1.5 py-[1px] font-medium"

  switch (def.configType) {
    case "condition":
      return (
        <div className="flex items-center gap-1 text-[11px]">
          <span className={pill}>{config.field || "…"}</span>
          <span className="opacity-70 text-[10px]">{config.operator}</span>
          <span className={pill}>{config.value || "…"}</span>
        </div>
      )
    case "role":
      return (
        <div className="flex items-center gap-1 text-[11px]">
          <Icon className="size-3 shrink-0" />
          <span className="font-medium">{def.label}</span>
          <span className="opacity-60 text-[10px]">{rolePrefix(variant)}</span>
          <span className={pill}>{config.role || "…"}</span>
        </div>
      )
    case "timing":
      return (
        <div className="flex items-center gap-1 text-[11px]">
          <Icon className="size-3 shrink-0" />
          <span className={pill}>{config.days || "…"}</span>
          <span className="opacity-60 text-[10px]">days before</span>
          <span className={pill}>{config.milestone || "…"}</span>
        </div>
      )
    case "milestone":
      return (
        <div className="flex items-center gap-1 text-[11px]">
          <Icon className="size-3 shrink-0" />
          <span className="opacity-60 text-[10px]">On</span>
          <span className={pill}>{config.milestone || "…"}</span>
        </div>
      )
    default:
      return (
        <div className="flex items-center gap-1.5 text-[11px]">
          <Icon className="size-3.5 shrink-0" />
          <span className="font-semibold">{def.label}</span>
        </div>
      )
  }
}

// ── Compact Block Stack (cards) ────────────────────────────────────────

function CompactBlockStack({ blocks }: { blocks: CanvasBlock[] }) {
  return (
    <div className="inline-flex flex-col">
      {blocks.map((block, i) => {
        const def = getDef(block.variant)
        const colors = COLOR_MAP[def.color] ?? { bg: "#6b7280", border: "#4b5563" }
        const isFirst = i === 0
        const isLast = i === blocks.length - 1
        return (
          <div key={block.id}>
            {!isFirst && (
              <div className="relative h-[6px]">
                <div className="absolute left-[18px] w-[14px] h-full rounded-b-[3px]" style={{ background: colors.bg }} />
              </div>
            )}
            <div
              className={cn(
                "px-3.5 py-[7px] text-white shadow-sm",
                isFirst && !isLast && "rounded-t-[14px] rounded-b-[3px]",
                isLast && !isFirst && "rounded-t-[3px] rounded-b-[14px]",
                isFirst && isLast && "rounded-[14px]",
                !isFirst && !isLast && "rounded-[3px]",
              )}
              style={{ background: colors.bg }}
            >
              <div className="flex items-center gap-1.5">
                <span className="font-extrabold text-[9px] uppercase tracking-widest opacity-60 mr-0.5 shrink-0">{def.keyword}</span>
                <CompactContent variant={block.variant} config={block.config} />
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ── Toggle ─────────────────────────────────────────────────────────────

function Toggle({ checked, onChange }: { checked: boolean; onChange: () => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={onChange}
      className={cn(
        "relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200",
        checked ? "bg-primary" : "bg-muted-foreground/30",
      )}
    >
      <span className={cn("pointer-events-none inline-block size-4 transform rounded-full bg-white shadow-lg ring-0 transition duration-200", checked ? "translate-x-4" : "translate-x-0")} />
    </button>
  )
}

// ── Rule Card ──────────────────────────────────────────────────────────

const DOT_GRID = {
  backgroundImage: "radial-gradient(circle, hsl(var(--muted-foreground) / 0.08) 1px, transparent 1px)",
  backgroundSize: "14px 14px",
}

function RuleCard({ rule, onToggle, onEdit, onDelete }: { rule: Rule; onToggle: () => void; onEdit: () => void; onDelete: () => void }) {
  return (
    <div className={cn("group rounded-xl border bg-card transition-all duration-200 hover:border-primary/20 hover:shadow-md hover:shadow-primary/5", rule.active ? "border-border" : "border-border/50 opacity-60")}>
      <div className="flex items-start justify-between gap-4 px-5 pt-4 pb-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <h3 className="font-medium text-sm text-foreground truncate">{rule.name}</h3>
            <span className={cn("inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider", rule.active ? "bg-emerald-500/10 text-emerald-500" : "bg-muted text-muted-foreground")}>
              {rule.active ? "Active" : "Inactive"}
            </span>
          </div>
          <p className="text-xs text-muted-foreground line-clamp-1">{rule.description}</p>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <Toggle checked={rule.active} onChange={onToggle} />
          <button onClick={onEdit} className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors opacity-0 group-hover:opacity-100">
            <Pencil className="size-3.5" />
          </button>
          <button onClick={onDelete} className="p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors opacity-0 group-hover:opacity-100">
            <Trash2 className="size-3.5" />
          </button>
        </div>
      </div>
      <div className="px-4 pb-4 pt-1">
        <div className="rounded-lg bg-muted/30 border border-border/40 p-3.5 overflow-x-auto" style={DOT_GRID}>
          <CompactBlockStack blocks={rule.blocks} />
        </div>
      </div>
    </div>
  )
}

// ── Page ────────────────────────────────────────────────────────────────

type PageView = { mode: "list" } | { mode: "builder"; editingRule: Rule | null }

const RulesPage = () => {
  const [rules, setRules] = useState<Rule[]>(PRESET_RULES)
  const [view, setView] = useState<PageView>({ mode: "list" })

  const handleSave = (rule: Rule) => {
    setRules((prev) => {
      const exists = prev.find((r) => r.id === rule.id)
      if (exists) return prev.map((r) => (r.id === rule.id ? rule : r))
      return [rule, ...prev]
    })
    setView({ mode: "list" })
    notifyFeature("Rule saved", true)
  }

  if (view.mode === "builder") {
    return <FlowBuilder initialRule={view.editingRule} onSave={handleSave} onCancel={() => setView({ mode: "list" })} />
  }

  return (
    <div className="h-full flex flex-col bg-background">
      <header className="shrink-0 border-b border-border bg-card/50">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="flex items-center justify-center size-10 rounded-md bg-muted">
                <ShieldCheck className="size-5 text-muted-foreground" />
              </div>
              <div>
                <h1 className="font-semibold text-foreground tracking-tight">Review Rules</h1>
                <p className="text-sm text-muted-foreground">Approval chains, advance triggers, and contract automation</p>
              </div>
            </div>
            <Button size="sm" onClick={() => setView({ mode: "builder", editingRule: null })}>
              <Plus className="size-4 mr-1.5" />
              Create Rule
            </Button>
          </div>
        </div>
      </header>

      <div className="flex-1 overflow-auto p-6">
        <div className="grid gap-4 max-w-5xl lg:grid-cols-2">
          {rules.map((rule) => (
            <RuleCard
              key={rule.id}
              rule={rule}
              onToggle={() => setRules((prev) => prev.map((r) => (r.id === rule.id ? { ...r, active: !r.active } : r)))}
              onEdit={() => setView({ mode: "builder", editingRule: rule })}
              onDelete={() => setRules((prev) => prev.filter((r) => r.id !== rule.id))}
            />
          ))}
        </div>
        {rules.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="flex items-center justify-center size-12 rounded-full bg-muted mb-4">
              <Zap className="size-6 text-muted-foreground" />
            </div>
            <h3 className="font-medium text-foreground mb-1">No rules yet</h3>
            <p className="text-sm text-muted-foreground mb-4">Create your first automation rule to streamline contract reviews.</p>
            <Button size="sm" onClick={() => setView({ mode: "builder", editingRule: null })}>
              <Plus className="size-4 mr-1.5" />
              Create Rule
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}

export default RulesPage
